import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Form, Input, Select, Switch, Modal } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQuery } from 'react-query';
import { getRequest, postRequest } from '../../../../../../core/apiRequests';
import { SettingsContent } from '../../../components/SettingsContent';
import DescriptionBox from '../../../../../../components/Layout/DescriptionBox';

export const StoreMessaging = ({ merchantId, storeId, managers, form }) => {
  const {
    data: methods,
    isFetching: isFetchingMethods,
    refetch: refetchMethods,
  } = useQuery(
    ['contactMethods', merchantId, storeId],
    async () => {
      const reqObj = {
        params: ['stores', 'contact-methods', merchantId, storeId],
        loadingStatusOff: true,
        query: {},
      };
      const data = await getRequest(reqObj);
      return data;
    },
    {
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const [modal, contextHolder] = Modal.useModal();

  const handleMethodToggle = useCallback((type, enabled) => {
    try {
      if (!type) return;
      if (methods?.find(method => method.type === type && method.isDefault === 1)) {
        modal.info({
          title: 'Default Method',
          content: 'You cannot disable the default contact method. Please set another method as default first.',
          centered: true,
        });
        return;
      }
      modal.confirm({
        title: 'Are you sure?',
        content: `Are you sure you want to ${enabled ? 'enable' : 'disable'} ${type}?`,
        okText: 'Yes',
        cancelText: 'No',
        centered: true,
        onOk: async () => {
          try {
            const reqObj = {
              params: ['stores', 'enable-contact-method', merchantId, storeId],
              data: [type, !enabled ? 1 : 0],
              loadingStatusOff: true,
            };
            await postRequest(reqObj);
            refetchMethods();
          } catch (error) {
            console.error('Error toggling method:', error);
          }
        },
      });
    } catch (error) {
      console.error('Error toggling method:', error);
    }
  }, []);

  const handleSetDefaultMethod = useCallback(async value => {
    if (!value) return; // do nothing if no value
    modal.confirm({
      title: 'Are you sure?',
      content: `Are you sure you want to set ${value} as the default contact method?`,
      centered: true,
      onOk: async () => {
        try {
          const reqObj = {
            params: ['stores', 'set-default', merchantId, storeId],
            data: [value?.toUpperCase()],
            loadingStatusOff: true,
          };
          await postRequest(reqObj);
          refetchMethods();
        } catch (error) {
          console.error('Error setting default method:', error);
        }
      },
    });
  }, []);
  return (
    <SettingsContent title="MESSAGING" icon={<FontAwesomeIcon icon={['far', 'message-lines']} />}>
      {contextHolder}
      <DescriptionBox
        className="w-100-P"
        title="Supported Channels"
        description={
          <>
            <span>
              Choose which communication channels are used for outbound messaging. If you support multiple channels,
              select a default way your messages will be delivered.
            </span>
            <br />
            <span>
              Looking for Facebook and other channels? Go to <Link to="/Help/integrations">Integrations</Link>.
            </span>
          </>
        }
        input={
          <div className="flex-row-center gap-16">
            {methods
              ?.filter(method => method.visible === 1)
              ?.map(method => (
                <Form.Item layout="vertical" style={{ marginBottom: 0 }} label={method.type} key={method.type}>
                  <Switch
                    checked={method.enabled}
                    onChange={checked => handleMethodToggle(method.type, checked)}
                    disabled={isFetchingMethods}
                    loading={isFetchingMethods}
                  />
                </Form.Item>
              ))}
            <Form.Item layout="vertical" style={{ marginBottom: 0 }} label="Default">
              <Select
                options={methods
                  ?.filter(method => method.enabled === 1)
                  ?.map(method => ({ label: method.type, value: method.type }))}
                style={{ width: '100px' }}
                value={methods?.find(method => method.isDefault === 1)?.type || null}
                onChange={value => handleSetDefaultMethod(value)}
                disabled={isFetchingMethods}
                loading={isFetchingMethods}
              />
            </Form.Item>
          </div>
        }
      />
      <DescriptionBox
        className="w-100-P"
        title="Default Associate for Outbound Messages"
        description="If an auto or mass message is sent to a client without an assigned associate, the placeholder for the associate's name will default to this person."
        input={
          <Form.Item name="manager_id" style={{ marginBottom: 0 }}>
            <Select options={managers} placeholder="Select Manager" popupMatchSelectWidth={false} />
          </Form.Item>
        }
      />
      <DescriptionBox
        className="w-100-P"
        title="After Hours Auto Response"
        description="Create a customized response and enter your store hours so when clients contact you via text, email, WhatsApp, or Webchat outside of store hours, they will receive your customized response to let them know."
        input={
          <Form.Item name="after_hours" valuePropName="checked">
            <Switch />
          </Form.Item>
        }
      />
      {!!Form.useWatch('after_hours', form) && (
        <div
          className="flex-col"
          style={{
            background: '#FAFAFA',
            padding: '32px',
            borderRadius: '4px',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          <DescriptionBox
            className="w-100-P"
            style={{ flexDirection: 'column', alignItems: 'flex-start' }}
            inputStyle={{ width: '100%' }}
            title="After Hours Message"
            description="When clients contact you outside of store hours, this is the response they’ll receive. We recommend keeping it informative and friendly."
            input={
              <Form.Item
                name="after_hours_msg"
                style={{ width: '100%', marginBottom: 0 }}
                rules={[{ message: 'After Hours Message is required', required: true }]}
              >
                <Input.TextArea style={{ width: '100%', height: '96px' }} />
              </Form.Item>
            }
          />
        </div>
      )}
    </SettingsContent>
  );
};
